import { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/layout";
import PrimaryButton from "components/Button/PrimaryButton";
import { formatCPF, formatCNPJ } from "@brazilian-utils/brazilian-utils";
import { CobrancaPessoas } from "features/cobranca/typings/index";

import useGetContatos from "features/contato/hooks/useGetContatos";
import useSaveContato from "features/contato/hooks/useSaveContato";
import useDeleteContato from "features/contato/hooks/useDeleteContato";
import { AiOutlinePlus } from "react-icons/ai";
import colors from "resources/theme/colors";
import ModalAddContato from "components/FormContatos/components/ModalAddContato";
import TabelaContato from "components/FormContatos/components/TabelaContato";
import { PessoaContato } from "features/unidades/typings/unidade";
import { showErrorAxios, showSuccess, showConfirm } from "infra/helpers/alerta";
import { useLoading } from "components/GlobalLoading";
import CardDefault from "components/Cards/CardDefault";
import { Avatar } from "@chakra-ui/react";

interface ConteudoDadosContatoProps {
  pessoa: CobrancaPessoas;
  title: string;
}

function ConteudoDadosContato({ pessoa, title }: ConteudoDadosContatoProps) {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [indexEdit, setIndexEdit] = useState<number | undefined>();

  const { data, isFetching, refetch } = useGetContatos(pessoa.id);

  const { mutate: handleSave, isLoading: isLoadingSave } = useSaveContato();
  const { mutate: handleDelete, isLoading: isLoadingDelete } =
    useDeleteContato();

  useLoading(isLoadingDelete);

  const onSubmit = (data: PessoaContato) => {
    handleSave(
      { ...data, pessoaId: pessoa.id },
      {
        onSuccess: async () => {
          setIndexEdit(undefined);

          setIsOpenModal(false);

          await showSuccess("Contato salva com sucesso");

          refetch();
        },
        onError: (res) => {
          showErrorAxios(res);
        },
      }
    );
  };

  const getDocumentoLabel = (documento: string | null) => {
    if (!documento) return "DOCUMENTO:";

    if (documento.length > 11) return "CNPJ:";

    return "CPF:";
  };

  const getDocumentoFormatted = (documento: string | null) => {
    if (!documento) return "--:";

    if (documento.length > 11) return formatCNPJ(documento);

    return formatCPF(documento);
  };

  return (
    <CardDefault>
      {isOpenModal ? (
        <ModalAddContato
          isOpen={isOpenModal}
          isLoading={isLoadingSave}
          onClose={() => setIsOpenModal(false)}
          onSaved={onSubmit}
          defaultData={
            indexEdit !== undefined && data ? data[indexEdit] : undefined
          }
        />
      ) : null}

      <Flex alignItems="center">
        <Avatar name={pessoa?.nome || ""} src={pessoa.imagemUrl} mr={2} />

        <Flex flexDir="column">
          <Text mt="8px">Nome: {pessoa?.nome}</Text>
          <Text color={"#8291A9"}>{title}</Text>
        </Flex>

        <Flex flexDir="column" ml="auto">
          <Text mt="8px" color={"#8291A9"}>
            {getDocumentoLabel(pessoa?.documento)}
          </Text>
          <Text>
            <Flex>{getDocumentoFormatted(pessoa?.documento)}</Flex>
          </Text>
        </Flex>
      </Flex>

      <Flex flexDir="column" mt={"50px"}>
        <Box>
          <Box display={"flex"} alignItems="center">
            <Text color={"#8291A9"} fontWeight={"500"} fontStyle={"italic"}>
              Contatos
            </Text>

            <PrimaryButton
              style={{
                minWidth: "21px",
                height: "21px",
                padding: 0,
                background: "transparent",
                color: `${colors.primary}`,
                borderRadius: "50%",
                border: `2px solid ${colors.primary}`,
                marginLeft: "8px",
              }}
              onClick={() => setIsOpenModal(true)}
            >
              <AiOutlinePlus />
            </PrimaryButton>
          </Box>
          <Box mt="8px">
            <TabelaContato
              onEdit={(index) => {
                setIndexEdit(index);
                setIsOpenModal(true);
              }}
              hideShadow
              onDelete={async (index) => {
                const { isConfirmed } = await showConfirm();

                if (isConfirmed && data) {
                  handleDelete(data[index].id, {
                    onSuccess: async () => {
                      await showSuccess();
                      refetch();
                    },
                    onError: (res) => {
                      showErrorAxios(res);
                    },
                  });
                }
              }}
              data={data ?? []}
              isLoading={isFetching}
            />
          </Box>
        </Box>
      </Flex>
    </CardDefault>
  );
}

export default ConteudoDadosContato;
